import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AUTH0 } from '../../utils/config';
import { useAuth } from '../../contexts/AuthContext';

/**
 * LoginHub - Handles Auth0 login flow including organization invitations
 * 
 * When a user clicks an invitation link from Auth0, they land on this page with:
 * - invitation: The invitation token
 * - organization: The Auth0 organization ID
 * - organization_name: The organization slug/name
 * 
 * This page immediately triggers loginWithRedirect with these params so Auth0
 * knows it's an invitation acceptance flow.
 */
const LoginHub = () => {
  const { loginWithRedirect, isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading } = useAuth0();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loginTriggered, setLoginTriggered] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Preparing login...');

  useEffect(() => {
    const initiateLogin = async () => {
      // If already authenticated, redirect to home
      if (auth0IsAuthenticated && !auth0IsLoading) {
        navigate('/', { replace: true });
        return;
      }

      // Don't trigger login while Auth0 is still initializing
      if (auth0IsLoading) {
        return;
      }

      // Prevent double-triggering
      if (loginTriggered) {
        return;
      }

      setLoginTriggered(true);

      // Extract invitation params from URL
      const invitation = searchParams.get('invitation');
      const organization = searchParams.get('organization');
      const organizationName = searchParams.get('organization_name');

      // Build authorization params
      const authorizationParams = {
        redirect_uri: AUTH0.redirectUri,
      };

      // If this is an invitation flow, include the invitation and organization params
      if (invitation && organization) {
        setStatusMessage('Processing invitation...');
        authorizationParams.invitation = invitation;
        authorizationParams.organization = organization;
        
        console.log('Processing Auth0 organization invitation', {
          organization,
          organizationName,
          hasInvitation: !!invitation
        });
      } else {
        setStatusMessage('Redirecting to login...');
      }

      try {
        await loginWithRedirect({
          authorizationParams
        });
      } catch (error) {
        console.error('Error initiating login:', error);
        setStatusMessage('Error initiating login. Please try again.');
      }
    };

    initiateLogin();
  }, [auth0IsAuthenticated, auth0IsLoading, loginWithRedirect, navigate, searchParams, loginTriggered]);

  // If already fully authenticated, show redirecting message
  if (isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Already logged in. Redirecting...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2
      }}
    >
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        {statusMessage}
      </Typography>
    </Box>
  );
};

export default LoginHub;
